/**
 * soundEngine — síntese de efeitos sonoros via Web Audio API pura.
 *
 * Singleton sem importações React. Todos os sons são gerados programaticamente
 * em PCM (sem arquivos externos), usando addOsc/addNoise + envelopes AD.
 *
 * Fluxo de volume: Player mantém escala 0–100 em playerVolumeRef.
 * Mute (playerMutedRef) e volume 0 silenciam os efeitos no momento do play.
 */

// ── Tipos públicos ─────────────────────────────────────────────────────────────

export type FutebolSoundId =
  | 'gol'
  | 'cartao_amarelo'
  | 'cartao_vermelho'
  | 'substituicao'
  | 'var_decisao'
  | 'acrescimos'
  | 'penalti'
  | 'escanteio'
  | 'lesao'
  | 'craque_bagre_intervalo'
  | 'bolao_resultado';

// ── Singleton state ────────────────────────────────────────────────────────────

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

let ctx:        AudioContext | null = null;
let masterGain: GainNode    | null = null;
let volSrc:     { readonly current: number | null } | null = null;
let mutedSrc:   { readonly current: boolean | null } | null = null;
let cacheBuilt = false;
const cache = new Map<FutebolSoundId, AudioBuffer>();

// ── Helpers de síntese ─────────────────────────────────────────────────────────

function sampleRate(): number { return ctx!.sampleRate; }

function makeBuf(durationSec: number): [AudioBuffer, Float32Array] {
  const n = Math.ceil(sampleRate() * durationSec);
  const b = ctx!.createBuffer(1, n, sampleRate());
  return [b, b.getChannelData(0)];
}

/**
 * Soma um oscilador senoidal ao buffer `out`.
 * Usa acumulação de fase para suportar varredura de frequência.
 * Envelope simples Attack-Decay (decai até 0).
 */
function addOsc(
  out:          Float32Array,
  startHz:      number,
  endHz:        number | null,
  startSec:     number,
  durationSec:  number,
  amplitude:    number,
  attackSec:    number,
  decaySec:     number,
): void {
  const sr     = sampleRate();
  const startI = Math.floor(startSec * sr);
  const endI   = Math.min(out.length, Math.floor((startSec + durationSec) * sr));
  const total  = endI - startI;
  const atkN   = Math.floor(attackSec * sr);
  const decN   = Math.floor(decaySec  * sr);
  let phase    = 0;

  for (let i = 0; i < total; i++) {
    const freq = endHz !== null
      ? startHz + (endHz - startHz) * (i / Math.max(1, total - 1))
      : startHz;
    phase += (2 * Math.PI * freq) / sr;

    const env = i < atkN
      ? i / Math.max(1, atkN)
      : Math.max(0, 1 - (i - atkN) / Math.max(1, decN));

    out[startI + i] += Math.sin(phase) * amplitude * env;
  }
}

/** Soma uma rajada de ruído branco com envelope AD ao buffer `out`. */
function addNoise(
  out:         Float32Array,
  startSec:    number,
  durationSec: number,
  amplitude:   number,
  attackSec:   number,
  decaySec:    number,
): void {
  const sr     = sampleRate();
  const startI = Math.floor(startSec * sr);
  const endI   = Math.min(out.length, Math.floor((startSec + durationSec) * sr));
  const total  = endI - startI;
  const atkN   = Math.floor(attackSec * sr);
  const decN   = Math.floor(decaySec  * sr);

  for (let i = 0; i < total; i++) {
    const env = i < atkN
      ? i / Math.max(1, atkN)
      : Math.max(0, 1 - (i - atkN) / Math.max(1, decN));
    out[startI + i] += (Math.random() * 2 - 1) * amplitude * env;
  }
}

/** Limita o sinal a [-1, 1] prevenindo clipping. */
function clamp(data: Float32Array): void {
  for (let i = 0; i < data.length; i++) {
    data[i] = Math.max(-1, Math.min(1, data[i]));
  }
}

// ── Síntese de cada som ────────────────────────────────────────────────────────

function synth_gol(): AudioBuffer {
  const [b, out] = makeBuf(1.2);
  const sr = sampleRate();
  // Kick drum: varredura de pitch 180 → 30 Hz com decaimento exponencial
  let phase = 0;
  const kickSamples = Math.floor(0.32 * sr);
  for (let i = 0; i < kickSamples; i++) {
    const t    = i / sr;
    const freq = 180 * Math.exp(-t * 12) + 30;
    phase += (2 * Math.PI * freq) / sr;
    out[i]   += Math.sin(phase) * 0.85 * Math.exp(-t * 7);
  }
  // Fanfarra ascendente: C5 → E5 → G5 → C6
  addOsc(out, 523.25, null, 0.18, 0.35, 0.55, 0.02, 0.33);
  addOsc(out, 659.25, null, 0.32, 0.35, 0.55, 0.02, 0.33);
  addOsc(out, 783.99, null, 0.46, 0.38, 0.55, 0.02, 0.36);
  addOsc(out, 1046.5, null, 0.65, 0.50, 0.45, 0.03, 0.47);
  clamp(out);
  return b;
}

function synth_cartao_vermelho(): AudioBuffer {
  const [b, out] = makeBuf(0.7);
  // Dois alarmes descendentes (sirene de árbitro)
  addOsc(out, 880, 580, 0.00, 0.28, 0.75, 0.008, 0.272);
  addOsc(out, 820, 540, 0.30, 0.28, 0.70, 0.008, 0.272);
  clamp(out);
  return b;
}

function synth_penalti(): AudioBuffer {
  const [b, out] = makeBuf(0.8);
  // Bumbada grave + dois blips de urgência
  addOsc(out, 220, 70, 0.00, 0.50, 0.65, 0.01, 0.49);
  addOsc(out, 750, null, 0.30, 0.20, 0.45, 0.01, 0.19);
  addOsc(out, 750, null, 0.55, 0.20, 0.40, 0.01, 0.19);
  clamp(out);
  return b;
}

function synth_cartao_amarelo(): AudioBuffer {
  const [b, out] = makeBuf(0.5);
  // Apito único curto
  addOsc(out, 740, null, 0.0, 0.38, 0.60, 0.01, 0.37);
  clamp(out);
  return b;
}

function synth_substituicao(): AudioBuffer {
  const [b, out] = makeBuf(0.6);
  // Swoosh de ruído + tom ascendente
  addNoise(out, 0.00, 0.30, 0.50, 0.01, 0.29);
  addOsc(out, 380, 620, 0.08, 0.38, 0.40, 0.02, 0.36);
  clamp(out);
  return b;
}

function synth_var_decisao(): AudioBuffer {
  const [b, out] = makeBuf(0.7);
  // Dois bips digitais (beep-beep)
  addOsc(out, 880, null, 0.00, 0.22, 0.65, 0.005, 0.215);
  addOsc(out, 880, null, 0.28, 0.22, 0.65, 0.005, 0.215);
  clamp(out);
  return b;
}

function synth_lesao(): AudioBuffer {
  const [b, out] = makeBuf(0.6);
  // "Ding" suave
  addOsc(out, 550, null, 0.0, 0.45, 0.55, 0.03, 0.42);
  clamp(out);
  return b;
}

function synth_escanteio(): AudioBuffer {
  const [b, out] = makeBuf(0.4);
  // Ping discreto
  addOsc(out, 900, null, 0.0, 0.28, 0.50, 0.008, 0.272);
  clamp(out);
  return b;
}

function synth_acrescimos(): AudioBuffer {
  const [b, out] = makeBuf(0.5);
  // Dois tons ascendentes (notificação leve)
  addOsc(out, 580, null, 0.00, 0.18, 0.45, 0.01, 0.17);
  addOsc(out, 800, null, 0.22, 0.22, 0.45, 0.01, 0.21);
  clamp(out);
  return b;
}

function synth_craque_bagre_intervalo(): AudioBuffer {
  const [b, out] = makeBuf(1.5);
  // Mini fanfarra de intervalo: C4 → G4 → C5 → G5
  addOsc(out, 261.63, null, 0.00, 0.40, 0.60, 0.02, 0.38);
  addOsc(out, 392.00, null, 0.22, 0.45, 0.55, 0.02, 0.43);
  addOsc(out, 523.25, null, 0.48, 0.50, 0.55, 0.02, 0.48);
  addOsc(out, 783.99, null, 0.85, 0.55, 0.45, 0.03, 0.52);
  // Harmônicos de textura
  addOsc(out, 523.25, null, 0.48, 0.46, 0.22, 0.02, 0.44);
  addOsc(out, 659.25, null, 0.65, 0.42, 0.20, 0.02, 0.40);
  clamp(out);
  return b;
}

function synth_bolao_resultado(): AudioBuffer {
  const [b, out] = makeBuf(1.2);
  // Frase triunfante ascendente: A4 → C5 → E5 → A5
  addOsc(out, 440.00, null, 0.00, 0.28, 0.55, 0.02, 0.26);
  addOsc(out, 523.25, null, 0.24, 0.28, 0.55, 0.02, 0.26);
  addOsc(out, 659.25, null, 0.48, 0.32, 0.55, 0.02, 0.30);
  addOsc(out, 880.00, null, 0.76, 0.38, 0.50, 0.03, 0.35);
  clamp(out);
  return b;
}

// ── Ganho relativo por tipo de evento ─────────────────────────────────────────

const GAINS: Record<FutebolSoundId, number> = {
  gol:                    1.00,
  cartao_vermelho:        0.80,
  penalti:                0.80,
  cartao_amarelo:         0.60,
  substituicao:           0.60,
  var_decisao:            0.70,
  lesao:                  0.60,
  escanteio:              0.50,
  acrescimos:             0.50,
  craque_bagre_intervalo: 0.70,
  bolao_resultado:        0.80,
};

// ── Build do cache ─────────────────────────────────────────────────────────────

function buildCache(): void {
  if (cacheBuilt) return;
  cacheBuilt = true;
  cache.set('gol',                    synth_gol());
  cache.set('cartao_vermelho',        synth_cartao_vermelho());
  cache.set('penalti',                synth_penalti());
  cache.set('cartao_amarelo',         synth_cartao_amarelo());
  cache.set('substituicao',           synth_substituicao());
  cache.set('var_decisao',            synth_var_decisao());
  cache.set('lesao',                  synth_lesao());
  cache.set('escanteio',              synth_escanteio());
  cache.set('acrescimos',             synth_acrescimos());
  cache.set('craque_bagre_intervalo', synth_craque_bagre_intervalo());
  cache.set('bolao_resultado',        synth_bolao_resultado());
}

// ── API pública ────────────────────────────────────────────────────────────────

function ensureContext(): AudioContext {
  if (!ctx) {
    const Ctor = window.AudioContext ?? window.webkitAudioContext ?? AudioContext;
    ctx        = new Ctor();
    masterGain = ctx.createGain();
    masterGain.connect(ctx.destination);
  }
  return ctx;
}

export const soundEngine = {
  /**
   * Registra o ref de volume do Player (escala 0–100).
   * Deve ser chamado uma única vez no mount do Player.
   * A leitura acontece no momento do play — sem listeners adicionais.
   */
  setVolumeRef(ref: { readonly current: number | null }): void {
    volSrc = ref;
  },

  setMutedRef(ref: { readonly current: boolean | null }): void {
    mutedSrc = ref;
  },

  /**
   * Toca um efeito sonoro por ID.
   * AudioContext e cache criados lazily na primeira chamada.
   * Erros de áudio nunca propagam para o Player — TV nunca deve quebrar por causa de som.
   */
  async playSound(id: FutebolSoundId): Promise<void> {
    try {
      const context = ensureContext();
      if (context.state === 'suspended') await context.resume();
      buildCache();

      const buffer = cache.get(id);
      if (!buffer || !masterGain) return;

      if (mutedSrc?.current) return;

      // Lê o volume do player no momento do play (0–100 → 0–1)
      const rawVol    = volSrc?.current ?? 100;
      const playerVol = Math.max(0, Math.min(1, rawVol / 100));

      masterGain.gain.setValueAtTime(playerVol, context.currentTime);

      const src  = context.createBufferSource();
      src.buffer = buffer;

      const gain = context.createGain();
      gain.gain.setValueAtTime(GAINS[id], context.currentTime);

      src.connect(gain);
      gain.connect(masterGain);
      src.start(0);

      // Desconecta os nós após o fim para evitar vazamento de memória
      src.onended = () => {
        src.disconnect();
        gain.disconnect();
      };
    } catch {
      // Silently ignore — áudio nunca deve quebrar o player
    }
  },
};
